import * as R from "remeda";
import { v4 as uuidv4 } from 'uuid';
import { ChatConfig, DebugChatResult, postApiChatDebugMultipleChat, TextProcessConfig } from "@/API";
import { useForm } from "@mantine/form";
import { useEffect, useRef, useState } from "react";
import { ChatModel } from "./ChatResultModel";
import { ActionIcon, Button, FileButton, Group, NumberInput, ScrollArea, Stack, TextInput, Tooltip, Switch, Box, Flex, Drawer } from "@mantine/core";
import { useImmer } from "use-immer";
import { notifications } from "@mantine/notifications";
import { IconArrowRight, IconCornerRightUp, IconHistory, IconInfoCircle, IconRepeat } from "@tabler/icons-react";
import { ChatRequestCard } from "./ChatCard";
import { TextSaveToFile } from "@/Helpers/SaveToFile";
import { AxiosError } from "axios";
import { SumTokenUsageDataWithModel } from "@/Helpers/TokenUsageHelper";
import { ChatBotTestList } from "./ChatBotTestList";
import { chatBotTestDB, UpsertChatBotTestChatRecord } from "@/Stores/ChatBotTest";
import { useDisclosure } from "@mantine/hooks";

export interface ChatBotFeatureConfig {
    chatRepetition: boolean
}

export function ChatBot(props: { workspaceId: string, chatBotDataId: string, chatConfig: ChatConfig, exportName: string, accessKey?: string, featureConfig?: ChatBotFeatureConfig }) {
    const [chatBotTestRecordId, setChatBotTestRecordId] = useState(uuidv4())
    const [chatBotTestList_opened, { open: chatBotTestList_open, close: chatBotTestList_close }] = useDisclosure(false);
    const form = useForm({
        initialValues: {
            text: '',
        }
    });
    const [chatRepetition, setChatRepetition] = useState(1)

    const chatControllerRef = useRef<AbortController | null>(null);
    const viewport = useRef<HTMLDivElement>(null);
    const scrollToBottom = () =>
        viewport.current!.scrollTo({ top: viewport.current!.scrollHeight, behavior: 'smooth' });

    const chatConfig = props.chatConfig

    const [chatResults, setChatResults] = useImmer<ChatModel[]>([]);
    const [textProcessConfig, setTextProcessConfig] = useImmer<TextProcessConfig>({
        forceRemoveMarkdown: true,
        foreceHansToTW: true
    });
    const [candidateChatResults, setCandidateChatResults] = useState<DebugChatResult[]>([])
    const resetRef = useRef<() => void>(null);
    useEffect(() => {
        scrollToBottom()
    }, [chatResults.length])
    useEffect(() => {
        if (chatResults && chatResults.length > 0)
            UpsertChatBotTestChatRecord({
                chatBotDataId: props.chatBotDataId,
                recordId: chatBotTestRecordId,
                histories: chatResults,
            })
    }, [chatResults])
    function GetRecords(beforeIndex: number | undefined = undefined) {
        var result = R.flatMap(R.takeLast(chatResults.slice(0, beforeIndex), chatConfig?.maxHistoryRecordsCount ?? 0), x => {
            if (chatConfig?.keepToolRecords)
                return [...x.response.newToolRecords ?? [], ...x.response.newRecords ?? []]
            else
                return [...x.response.newRecords ?? []]
        })
        var output = []
        var toolOutput = []
        for (var i = 0; i < result.length; i++) {
            if (result[i].role == "Tool")
                toolOutput.push(result[i])
            else
                output.push(result[i])
        }
        return {
            records: output,
            toolRecords: toolOutput
        }
    }
    async function EditResult(result: ChatModel) {
        setCandidateChatResults([])
        try {
            var index = chatResults.findIndex(x => x.id == result.id)
            var records = GetRecords(index)
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index < 0) return s;
                var newData = s.slice(0, index)
                var addData = { ...result }
                addData.responseLoading = true
                addData.response = {}
                addData.responseFail = false
                addData.responseTime = 0
                newData.push(addData)
                return newData
            })
            const start = Date.now()
            chatControllerRef.current?.abort()
            chatControllerRef.current = new AbortController();
            var response = await postApiChatDebugMultipleChat({
                message: result.requestText,
                records: records.records,
                toolRecords: records.toolRecords,
                textProcessConfig: textProcessConfig,
                publicAccessKey: props.accessKey
            }, {
                chatBotDataId: props.chatBotDataId, count: chatRepetition
            }, {
                headers: { "Content-Type": "application/json" },
                signal: chatControllerRef.current.signal
            })
            const finish = Date.now()
            const time = (finish - start) / 1000
            setCandidateChatResults(response.data.splice(1))
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index >= 0) {
                    s[index].response = response.data[0] ?? {}
                    s[index].responseTime = time
                    s[index].responseLoading = false
                }
            })
            scrollToBottom()
        } catch (e) {
            var error = e as AxiosError
            notifications.show({ message: "傳送失敗", color: "red" })
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index >= 0) {
                    s[index].responseFail = true
                    s[index].responseFailMsg = JSON.stringify(error.response?.data)
                    s[index].responseLoading = false
                }
            })
        }
    }
    // https://github.com/orgs/mantinedev/discussions/3588#discussioncomment-11740908
    return <>
        <Group justify="space-between" my={5}>
            <Group gap={5}>
                <Tooltip color="gray" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={`QA Count:${chatResults.length}\nInputTokens:${R.sumBy(chatResults, x => SumTokenUsageDataWithModel(x.response.tokenUsage ?? []).inputTokens ?? 0)}\nAverageInputTokens:${Math.round(R.meanBy(chatResults, x => SumTokenUsageDataWithModel(x.response.tokenUsage ?? []).inputTokens ?? 0))}\nOutputTokens:${R.sumBy(chatResults, x => SumTokenUsageDataWithModel(x.response.tokenUsage ?? []).outputTokens ?? 0)}\nAverageOutputTokens:${Math.round(R.meanBy(chatResults, x => SumTokenUsageDataWithModel(x.response.tokenUsage ?? []).outputTokens ?? 0))}`}>
                    <IconInfoCircle color="gray" />
                </Tooltip>
                <Button disabled={chatResults.length == 0} size="compact-md" variant="light" onClick={() => {
                    var time = new Date()
                        .toISOString()
                        .replace(/:/g, "-")
                        .replace(/\.\d+Z$/, "Z");
                    TextSaveToFile(`${props.exportName}_${time}.json`, JSON.stringify(chatResults))
                }}>
                    匯出
                </Button>
                <FileButton resetRef={resetRef} onChange={async f => {
                    resetRef.current?.()
                    var text = await f?.text()
                    if (text) {
                        chatControllerRef.current?.abort()
                        setChatResults(JSON.parse(text))
                        setCandidateChatResults([])
                    }
                }} accept="application/json">
                    {(props) =>
                        <Button {...props} size="compact-md" variant="light">
                            匯入
                        </Button>
                    }
                </FileButton>
                <Switch
                    label="簡轉繁"
                    checked={textProcessConfig.foreceHansToTW}
                    onChange={x => {
                        setTextProcessConfig(c => {
                            c.foreceHansToTW = x.target.checked
                        })
                    }}
                />
                <Switch
                    label="去除Markdown"
                    checked={textProcessConfig.forceRemoveMarkdown}
                    onChange={x => {
                        setTextProcessConfig(c => {
                            c.forceRemoveMarkdown = x.target.checked
                        })
                    }}
                />
            </Group>
            <Button variant="light" onClick={chatBotTestList_open} leftSection={<IconHistory />}>
                聊天紀錄
            </Button>
        </Group>
        <ScrollArea viewportRef={viewport} offsetScrollbars flex={1}>
            <Stack>
                {chatResults.map((result, idx) => <Stack key={`chat_${idx}`} >
                    <ChatRequestCard workspaceId={props.workspaceId} chatModel={result} onReload={async () => {
                        await EditResult(result)
                    }} onEditText={async t => {
                        var r = R.clone(result)
                        r.requestText = t
                        await EditResult(r)
                    }} />
                </Stack>)}
                {
                    candidateChatResults.length > 0 && <>
                        <ActionIcon variant="subtle" m={0} onClick={() => {
                            setCandidateChatResults([])
                        }}>
                            <IconCornerRightUp stroke={3} />
                        </ActionIcon>
                    </>
                }
                {
                    candidateChatResults.map((result, idx) => <Stack>
                        <Group gap={10} style={{ flexWrap: "nowrap" }}>
                            <ActionIcon variant="subtle" m={0} onClick={() => {
                                setChatResults(x => {
                                    x[x.length - 1].response = result
                                })
                                setCandidateChatResults([])
                            }}>
                                <IconArrowRight stroke={3} />
                            </ActionIcon>
                            <ChatRequestCard workspaceId={props.workspaceId} chatModel={{
                                id: "",
                                response: result,
                                responseTime: R.last(chatResults)?.responseTime,
                                responseFail: false,
                                responseLoading: false
                            }} />
                        </Group>
                    </Stack>)
                }
            </Stack>
        </ScrollArea>
        <Group gap={5}>
            <form style={{ flex: 1 }} onSubmit={form.onSubmit(async (values) => {
                setCandidateChatResults([])
                form.setFieldValue("text", "")
                if (values.text) {
                    const id = uuidv4()
                    setChatResults(x => {
                        x.push({
                            id: id,
                            requestText: values.text,
                            response: {},
                            responseLoading: true,
                            responseFail: false,
                        })
                    })
                    try {
                        const start = Date.now()
                        chatControllerRef.current?.abort()
                        chatControllerRef.current = new AbortController();
                        var records = GetRecords();
                        var response = await postApiChatDebugMultipleChat({
                            message: values.text,
                            records: records.records,
                            toolRecords: records.toolRecords,
                            textProcessConfig: textProcessConfig,
                            publicAccessKey: props.accessKey
                        }, {
                            chatBotDataId: props.chatBotDataId, count: chatRepetition
                        }, {
                            headers: { "Content-Type": "application/json" },
                            signal: chatControllerRef.current.signal
                        })

                        // console.log(response)
                        const finish = Date.now()
                        const time = (finish - start) / 1000
                        setCandidateChatResults(response.data.splice(1))
                        setChatResults(x => {
                            var index = x.findIndex(x => x.id == id)
                            if (index >= 0) {
                                x[index].response = response.data[0] ?? {}
                                x[index].responseTime = time
                                x[index].responseLoading = false
                            }
                        })
                        scrollToBottom()
                    } catch (e) {
                        notifications.show({ message: "傳送失敗", color: "red" })
                        setChatResults(x => {
                            var error = e as AxiosError
                            var index = x.findIndex(x => x.id == id)
                            if (index >= 0) {
                                x[index].responseFail = true
                                x[index].responseFailMsg = JSON.stringify(error.response?.data)
                                x[index].responseLoading = false
                            }
                        })
                    }
                }
            })}>
                <TextInput
                    placeholder="傳送訊息"
                    radius="lg"
                    {...form.getInputProps('text')}
                />
            </form>
            {
                props.featureConfig?.chatRepetition &&
                <Tooltip color="gray" label="重複問答次數">
                    <NumberInput w={80} radius="lg" min={1} max={20} leftSection={<IconRepeat />} variant="filled" value={chatRepetition} onChange={x => {
                        setChatRepetition(x as number)
                    }} />
                </Tooltip>
            }
        </Group>
        <Drawer opened={chatBotTestList_opened} onClose={chatBotTestList_close} title="聊天紀錄" position="right" size={350} >
            <ChatBotTestList id={props.chatBotDataId} selectRecordId={chatBotTestRecordId} onSelect={(record => {
                chatControllerRef.current?.abort()
                setChatBotTestRecordId(record.recordId)
                setChatResults(record.histories)
                setCandidateChatResults([])
            })} onDeselect={() => {
                chatControllerRef.current?.abort()
                setChatBotTestRecordId(uuidv4())
                setChatResults([])
                setCandidateChatResults([])
            }} />
        </Drawer>
    </>
}